import { useEffect, useState } from "react";
import { getGlobalOperationsData } from "../../../api/globalOperationsService";

function GlobalOperationsCenterPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        try {
            setLoading(true);
            const result = await getGlobalOperationsData();
            setData(result);
        } catch (err) {
            console.error("Global Operations load failed", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={{ padding: 20 }}>Loading Global Operations Center...</div>;
    if (!data) return <div style={{ padding: 20 }}>Operations data not available.</div>;

    return (
        <div style={{ padding: 20 }}>
            <h2>Global Operations Center</h2>

            <p style={{ color: "#666" }}>
                Mission control dashboard integrating logistics, AI, IoT, blockchain, trust, prediction and security.
            </p>

            <div style={gridStyle}>
                <Card title="Active Shipments" value={data.activeShipments} color="#1976D2" />
                <Card title="Active Orders" value={data.activeOrders} color="#43A047" />
                <Card title="IoT Online" value={`${data.iotOnline}/${data.iotTotal}`} color="#7B1FA2" />
                <Card title="Blockchain Events" value={data.blockchainEvents} color="#546E7A" />
                <Card title="Trust Score" value={`${data.trustScore}%`} color={trustColor(data.trustScore)} />
                <Card title="AI Risk" value={`${data.aiRisk}%`} color={data.aiRisk > 25 ? "#C62828" : "#2E7D32"} />
                <Card title="Predicted Delays" value={data.predictedDelays} color="#EF6C00" />
                <Card title="ETA Accuracy" value={`${data.etaAccuracy}%`} color="#00897B" />
            </div>

            <div style={layoutStyle}>
                <Panel title="Mission Status">
                    <Status label="Platform" value="Operational" ok />
                    <Status label="AI Engine" value={data.aiRisk > 25 ? "Review Required" : "Stable"} ok={data.aiRisk <= 25} />
                    <Status label="Trust Engine" value={data.trustScore >= 70 ? "Trusted" : "Low Trust"} ok={data.trustScore >= 70} />
                    <Status label="IoT Network" value={data.iotOnline > 0 ? "Live" : "Offline"} ok={data.iotOnline > 0} />
                    <Status label="Blockchain" value={data.blockchainEvents > 0 ? "Auditing" : "No Events"} ok={data.blockchainEvents > 0} />
                </Panel>

                <Panel title="Active Shipments">
                    {data.shipments.slice(0, 8).map((s) => (
                        <Row
                            key={s.id}
                            title={s.trackingNumber || `Shipment #${s.id}`}
                            subtitle={s.status || s.shipmentStatus || "�"}
                        />
                    ))}
                    {data.shipments.length === 0 && <p>No active shipments.</p>}
                </Panel>

                <Panel title="IoT Devices">
                    {data.devices.slice(0, 8).map((d) => (
                        <Row
                            key={d.id || d.trackingNumber}
                            title={d.trackingNumber || d.name || "IoT Device"}
                            subtitle={d.lastUpdated ? `Updated: ${d.lastUpdated}` : "No signal"}
                        />
                    ))}
                    {data.devices.length === 0 && <p>No IoT devices.</p>}
                </Panel>

                <Panel title="Critical Signals">
                    <Status label="Critical Events" value={data.criticalEvents} ok={data.criticalEvents === 0} />
                    <Status label="Notifications" value={data.notificationsCount} ok />
                    <Status label="Predicted Delays" value={data.predictedDelays} ok={data.predictedDelays === 0} />
                    <Status label="AI Risk" value={`${data.aiRisk}%`} ok={data.aiRisk <= 25} />
                </Panel>
            </div>
        </div>
    );
}

function Card({ title, value, color }) {
    return (
        <div style={{ ...cardStyle, borderTop: `6px solid ${color}` }}>
            <div style={{ color: "#666", fontSize: 14 }}>{title}</div>
            <div style={{ fontSize: 30, fontWeight: "bold", marginTop: 10 }}>{value}</div>
        </div>
    );
}

function Panel({ title, children }) {
    return (
        <div style={panelStyle}>
            <h3>{title}</h3>
            <div style={{ display: "grid", gap: 10 }}>{children}</div>
        </div>
    );
}

function Row({ title, subtitle }) {
    return (
        <div style={rowStyle}>
            <b>{title}</b>
            <span style={{ color: "#777", fontSize: 13 }}>{subtitle}</span>
        </div>
    );
}

function Status({ label, value, ok }) {
    return (
        <div style={statusStyle}>
            <b>{label}</b>
            <span style={{ color: ok ? "green" : "red", fontWeight: "bold" }}>{value}</span>
        </div>
    );
}

function trustColor(score) {
    if (score >= 85) return "#2E7D32";
    if (score >= 70) return "#EF6C00";
    return "#C62828";
}

const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginTop: 20,
};

const layoutStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: 20,
    marginTop: 24,
};

const cardStyle = {
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};

const panelStyle = {
    background: "white",
    borderRadius: 12,
    padding: 18,
    boxShadow: "0 4px 12px rgba(0,0,0,.08)",
};

const rowStyle = {
    display: "grid",
    gap: 4,
    borderBottom: "1px solid #eee",
    paddingBottom: 8,
};

const statusStyle = {
    display: "flex",
    justifyContent: "space-between",
    borderBottom: "1px solid #eee",
    paddingBottom: 8,
};

export default GlobalOperationsCenterPage;